import { Button, Checkbox, Input, PageHeader, Space, Table } from 'antd';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import ReplaceStrings from '../../../../config/replaceStrings';
import Routes from '../../../../config/routes';
import { translations } from '../../../../config/translations';
import { RouteParamsGeneric } from '../../../../core/models/RouteParams';
import { showConfirm, showError, showSuccess } from '../../../../helpers/NotificationHelper';
import { notifyTransfersChanged } from '../../helpers';
import { getTableLocale } from '../../../../helpers/TableHelper';
import {
    ApiException,
    ReceiveLine,
    ReceiveToolTransferCommand,
    ToolTransferDetailVm,
    ToolTransferItemVm,
    ToolTransfersClient,
} from '../../../../utils/api';

interface Props extends RouteComponentProps<RouteParamsGeneric> {}

interface LineState {
    received: boolean;
    lostReason: string;
}

interface State {
    transfer?: ToolTransferDetailVm;
    lines: Record<number, LineState>;
    isSaving: boolean;
}

class TransferReceive extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            lines: {},
            isSaving: false,
        };
    }

    public componentDidMount = () => {
        void this.getTransfer();
    };

    private getTransfer = async (): Promise<void> => {
        const {
            match: {
                params: { entityId },
            },
        } = this.props;

        if (!entityId) return;

        const transfer = await new ToolTransfersClient().getById(+entityId);

        // Everything is ticked as arrived by default; the user only unticks the exceptions.
        const lines: Record<number, LineState> = {};
        (transfer.items || []).forEach((item) => {
            lines[item.id] = { received: true, lostReason: '' };
        });

        this.setState({ transfer, lines });
    };

    private setLine = (itemId: number, patch: Partial<LineState>) => {
        this.setState((prev) => ({
            lines: {
                ...prev.lines,
                [itemId]: { ...prev.lines[itemId], ...patch },
            },
        }));
    };

    private handleSubmit = () => {
        const { lines } = this.state;

        // Same rule the server enforces: no reason, no way to mark a tool as missing.
        const missingReason = Object.values(lines).some(
            (line) => !line.received && !line.lostReason.trim()
        );

        if (missingReason) {
            showError(translations.transfers.lostReasonRequired);
            return;
        }

        // Irreversible: tools move warehouse and the missing ones are archived.
        showConfirm(() => void this.receive(), translations.transfers.receiveConfirm);
    };

    private receive = async () => {
        const { transfer, lines } = this.state;
        const { history } = this.props;

        if (!transfer) return;

        const entries = Object.entries(lines);

        this.setState({ isSaving: true });

        try {
            await new ToolTransfersClient().receive(
                transfer.id,
                new ReceiveToolTransferCommand({
                    id: transfer.id,
                    lines: entries.map(
                        ([itemId, line]) =>
                            new ReceiveLine({
                                itemId: parseInt(itemId),
                                received: line.received,
                                lostReason: line.received ? undefined : line.lostReason,
                            })
                    ),
                })
            );

            showSuccess(translations.transfers.receiveSuccess);
            notifyTransfersChanged();
            history.push(
                Routes.ROUTE_TRANSFERS_READ.replace(
                    ReplaceStrings.ENTITY_ID,
                    transfer.id.toString()
                )
            );
        } catch (error) {
            // Only on failure: on success the page has already navigated away and unmounted.
            this.setState({ isSaving: false });

            if (error instanceof ApiException) {
                showError(error.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        }
    };

    public render(): React.ReactElement {
        const { transfer, lines, isSaving } = this.state;
        const { history } = this.props;

        if (!transfer) {
            return <PageHeader title={translations.transfers.receiveTitle} />;
        }

        const columns = [
            {
                title: translations.transfers.tools,
                dataIndex: 'toolName',
            },
            {
                title: translations.transfers.arrived,
                key: 'received',
                width: 100,
                render: (_: unknown, item: ToolTransferItemVm): React.ReactElement => (
                    <Checkbox
                        checked={lines[item.id]?.received ?? true}
                        onChange={(e) =>
                            this.setLine(item.id, {
                                received: e.target.checked,
                                lostReason: e.target.checked
                                    ? ''
                                    : lines[item.id]?.lostReason ?? '',
                            })
                        }
                    />
                ),
            },
            {
                title: translations.transfers.lostReason,
                key: 'lostReason',
                render: (_: unknown, item: ToolTransferItemVm): React.ReactElement | null =>
                    lines[item.id]?.received === false ? (
                        <Input
                            value={lines[item.id]?.lostReason}
                            placeholder={translations.transfers.lostReasonPlaceholder}
                            onChange={(e) =>
                                this.setLine(item.id, { lostReason: e.target.value })
                            }
                        />
                    ) : null,
            },
        ];

        return (
            <>
                <PageHeader
                    title={translations.transfers.receiveTitle}
                    subTitle={transfer.fromName + ' → ' + transfer.toName}
                    onBack={() =>
                        history.push(
                            Routes.ROUTE_TRANSFERS_READ.replace(
                                ReplaceStrings.ENTITY_ID,
                                transfer.id.toString()
                            )
                        )
                    }
                />

                <Table
                    locale={getTableLocale()}
                    columns={columns}
                    dataSource={transfer.items}
                    rowKey={(item: ToolTransferItemVm): string => item.id.toString()}
                    pagination={false}
                    bordered
                    size="small"
                />

                <Space style={{ marginTop: 24 }}>
                    <Button type="primary" loading={isSaving} onClick={this.handleSubmit}>
                        {translations.transfers.receive}
                    </Button>
                    <Button
                        onClick={() =>
                            history.push(
                                Routes.ROUTE_TRANSFERS_READ.replace(
                                    ReplaceStrings.ENTITY_ID,
                                    transfer.id.toString()
                                )
                            )
                        }
                    >
                        {translations.general.cancel}
                    </Button>
                </Space>
            </>
        );
    }
}

export default withRouter(TransferReceive);
